import { useParams } from 'react-router-dom';
import { ErrorPage } from '@screentone/addon-auth-wrapper';

import { ResponsiveLoader } from 'components';
import { ViewPublishedURL } from 'components/context-menu-actions/view-published-url/ViewPublishedURL';
import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import { AllessehContentUpdateProvider } from 'contexts/allesseh-content-update/AllessehContentUpdateContext';
import {
  ContextMenuActionsProvider,
  MenuActions,
  MenuActionsKeys
} from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { DataModelProvider, DataModelType } from 'contexts/datamodel/DataModelContext';
import { DragAndDropProvider } from 'contexts/drag-and-drop/DragAndDropContext';
import { PageTypeSettingsProvider } from 'contexts/page-type-settings/PageTypeSettingsContext';
import { SummarianProvider } from 'contexts/summarian/SummarianContext';
import {
  ArticleItem,
  BaseMetadata,
  NewsletterPageContainer,
  PageType,
  useNewsletterByIdQuery
} from 'data/generated/graphql';
import { useEnabledNewsletter } from 'features/newsletters/hooks/useEnabledNewsletter';
import { ReadOnlyNewsletter } from 'features/newsletters/ReadOnlyNewsletter';
import { useConvertedProperty } from 'hooks';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { safelyParseContent } from 'utils/temp';

const contextActions: MenuActions = {
  [MenuActionsKeys.DraftArticle]: [
    {
      component: ({ entity }) => {
        const article = entity as ArticleItem;
        return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
      }
    }
  ]
};

const SavedNewsletterPage = () => {
  const { campaignId } = useParams();
  const currentProperty = useConvertedProperty();

  const { data, isLoading, isError, error } = useNewsletterByIdQuery(
    { id: campaignId ?? '', publicationKey: currentProperty ?? '' },
    { enabled: !!campaignId && !!currentProperty }
  );

  const { isLoading: isLoadingEnabledNewsletter, enabledNewsletter } = useEnabledNewsletter();

  if (!campaignId || !currentProperty) {
    return <></>;
  }

  if (isError && error instanceof Error && error.message === 'Not found') {
    return <ErrorPage type="404" />;
  }

  if (isError || !enabledNewsletter) {
    return <ErrorPage type="500" />;
  }

  if (isLoading || isLoadingEnabledNewsletter) {
    return (
      <PageRootContainer>
        <ResponsiveLoader />
      </PageRootContainer>
    );
  }

  return (
    <AllessehContentUpdateProvider>
      <SummarianProvider>
        <DragAndDropProvider>
          <PageRootContainer noBottomMargin>
            <DataModelProvider
              type={DataModelType.NewsletterDTO}
              root={data.newsletterById.root as NewsletterPageContainer}
              metadata={data.newsletterById.metadata as BaseMetadata}
              isReadOnly
            >
              <ContextMenuActionsProvider actions={contextActions}>
                <PageTypeSettingsProvider pageType={PageType.Newsletter}>
                  <ReadOnlyNewsletter isScheduled={false} />
                </PageTypeSettingsProvider>
              </ContextMenuActionsProvider>
            </DataModelProvider>
          </PageRootContainer>
        </DragAndDropProvider>
      </SummarianProvider>
    </AllessehContentUpdateProvider>
  );
};

export default SavedNewsletterPage;
